/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { FormikHelpers, useFormik } from 'formik';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { Box, Button, Card, Tooltip, Typography } from '@mui/material';

import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { MarkdownEditor } from '../../../../components/MarkdownEditor/MarkdownEditor';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../../../i18n';
import { SupportJiraApiService } from '../../../../services/SupportJiraApiService';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { JiraCustomerSupportRequestComment } from '../../../../types/support/JiraSupport';
import { fetchJiraRequestComments } from '../../../../utils/queryFns';
import { reactToCreateJiraComment } from '../../../../utils/reactions';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useFormatDate } from '../../../../utils/useFormatDate';
import { useJiraSupportStyles } from '../styles';
import { commentInitialValues, commentValidationSchema, TicketCommentsProps } from '../types';

export const TicketComments = memo<TicketCommentsProps>(({ id }) => {
  const [errorModal, setErrorModal] = useState<string | null>(null);

  const classes = useJiraSupportStyles();
  const { t } = useTranslation([Namespaces.General, Namespaces.Common]);
  const { formatDate } = useFormatDate();

  const { setApiError } = useApiValidation();

  const handleOnSuccess = () => reactToCreateJiraComment();

  const handleError = useCallback(
    (error: ApiClientError) => {
      const validation = setApiError(error);
      if (validation.errorType === 'unexpected' || validation.errorType === 'common') {
        setErrorModal(t(`${Namespaces.Common}:errors.unexpected_error`));
      }
    },
    [setApiError, t],
  );

  const { data, isLoading: isLoadingComments } = useQuery(
    [fetchJiraRequestComments.key, { id }],
    fetchJiraRequestComments.fn,
    { refetchOnWindowFocus: true, refetchOnMount: true },
  );

  const ticketCommentsData = useMemo(() => data?.payload ?? [], [data]);

  const [createComment, { isLoading: isCreateCommentLoading }] = useMutation(
    SupportJiraApiService.createRequestComment,
    {
      onSuccess: handleOnSuccess,
      onError: (error: ApiClientError) => handleError(error),
    },
  );

  const isLoading = useMemo(
    () => isLoadingComments || isCreateCommentLoading,
    [isLoadingComments, isCreateCommentLoading],
  );

  const onCommentSubmit = useCallback(
    async (
      { body }: JiraCustomerSupportRequestComment,
      { resetForm }: FormikHelpers<JiraCustomerSupportRequestComment>,
    ) => {
      await createComment({
        id,
        body,
      });
      resetForm();
    },
    [createComment, id],
  );

  const { values, handleSubmit, dirty, handleChange } = useFormik<JiraCustomerSupportRequestComment>({
    validationSchema: commentValidationSchema,
    enableReinitialize: true,
    initialValues: commentInitialValues,
    onSubmit: onCommentSubmit,
  });

  const handleCloseError = () => setErrorModal(null);

  const getLabelKey = (key: string, label?: boolean) =>
    label ? `${Namespaces.General}:jira_support.labels.${key}` : `${Namespaces.General}:jira_support.${key}`;

  const renderComments = useMemo(
    () =>
      ticketCommentsData.length ? (
        ticketCommentsData.map(({ id, body, createdAt, updatedAt, author }) => (
          <Card key={id} classes={{ root: classes.card }}>
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
              <Box display="flex" flexDirection="row" alignItems="center" gap={8}>
                <img src={author.avatar} alt="User avatar" className={classes.commentAvatar} />
                <Tooltip title={author.email} placement="top" arrow>
                  <Typography variant="subtitle2" className={classes.commentName}>
                    {author.name ? author.name : t(getLabelKey('yourself'))}
                  </Typography>
                </Tooltip>
              </Box>
              <Tooltip title={`${t(getLabelKey('updatedAt'))}: ${formatDate(updatedAt)}`} placement="top" arrow>
                <Typography variant="subtitle2" className={classes.subHeader}>
                  {formatDate(createdAt)}
                </Typography>
              </Tooltip>
            </Box>
            <div dangerouslySetInnerHTML={{ __html: body.rendered }} />
          </Card>
        ))
      ) : (
        <Card classes={{ root: classes.card }}>
          <Typography variant="subtitle2">{t(getLabelKey('no_comments'))}</Typography>
        </Card>
      ),
    [classes.card, classes.subHeader, classes.commentAvatar, classes.commentName, formatDate, t, ticketCommentsData],
  );

  return (
    <>
      <HUSpin spinning={isLoading} delay={50}>
        <Box className={classes.gapThree}>
          <Box className={classes.gapOne}>{renderComments}</Box>
          <form onSubmit={handleSubmit}>
            <Box className={classes.gapOne}>
              <MarkdownEditor value={values.body} onChange={handleChange('body')} />
              <Box className={classes.gapOne} style={{ justifyContent: 'end' }}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="medium"
                  disabled={isLoading || !dirty}
                  aria-label={t(getLabelKey('send', true))}
                >
                  {t(getLabelKey('send', true))}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </HUSpin>
      <SimpleErrorModal open={!!errorModal?.length} message={errorModal ?? ''} t={t} onOk={handleCloseError} />
    </>
  );
});
